import axios from 'axios'
import React, { useState, useEffect, useRef, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { FiChevronDown, FiCheck, FiUser, FiX } from 'react-icons/fi'
import BASE_URL from '../../../config/config'
import Loader from '../../../components/Loader'
import Popup from '../../../components/Popup'

// Desplegable con pill coloreada + check, mismo lenguaje visual que el resto del panel admin
const EstadoSelect = ({ value, options, onChange, disabled }) => {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  const current = options.find(o => o.value === value)

  useEffect(() => {
    if (!open) return
    const handleClickOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    const handleKeyDown = (e) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open])

  return (
    <div className="desplegable-wrap" ref={ref}>
      <button
        type="button"
        className={`desplegable-trigger ${value || ''}`}
        onClick={() => !disabled && setOpen(v => !v)}
        disabled={disabled}
      >
        {current ? current.label : 'Seleccionar'}
        <FiChevronDown className="desplegable-flecha" />
      </button>
      {open && (
        <div className="desplegable-menu">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`desplegable-item ${opt.value === value ? 'activo' : ''}`}
              onClick={() => { onChange(opt.value); setOpen(false) }}
            >
              <span className="desplegable-check">{opt.value === value && <FiCheck />}</span>
              <span>{opt.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// Toggle Sí/No para el campo Facturado
const FacturadoToggle = ({ value, onToggle, disabled }) => {
  const isOn = Number(value) === 1
  return (
    <button
      type="button"
      className={`toggle-facturado ${isOn ? 'on' : 'off'}`}
      onClick={() => onToggle(isOn ? 0 : 1)}
      title="Cambiar facturado"
      disabled={disabled}
      aria-busy={disabled}
    >
      {disabled ? 'Guardando…' : (isOn ? 'Sí' : 'No')}
    </button>
  )
}

const Proyectos = () => {
  const navigate = useNavigate()
  const [proyectos, setProyectos] = useState([])
  const [isLoading, setIsLoading] = useState(true)

  // Filtros
  const [searchText, setSearchText] = useState('')
  const [sortOrder, setSortOrder] = useState('newest')
  const [tipoFilter, setTipoFilter] = useState('Todos')   // Todos | desarrollo | diseno | marketing
  const [archFilter, setArchFilter] = useState('no')      // no | si | todos

  // Usuarios para asignación
  const [users, setUsers] = useState([])

  // Modal de asignación de usuario
  const [userModalOpen, setUserModalOpen] = useState(false)
  const [userModalProject, setUserModalProject] = useState(null)
  const [userQuery, setUserQuery] = useState('')
  const [userPage, setUserPage] = useState(1) // cada página = 10 usuarios

  // Deshabilitar el toggle por proyecto mientras guarda
  const [savingFacturado, setSavingFacturado] = useState({}) // { [idProyecto]: true|false }

  const estados = [
    { value: 'sin_ejecutar', label: 'Sin ejecutar' },
    { value: 'en_proceso', label: 'En proceso' },
    { value: 'aceptado', label: 'Aceptado' }
  ]

  const fetchProyectos = () => {
    return axios.get(`${BASE_URL}/proyectos`)
      .then(response => {
        const data = response.data.proyectos ? response.data.proyectos : response.data
        setProyectos(Array.isArray(data) ? data : [])
      })
      .catch(error => console.error(error))
  }

  useEffect(() => {
    fetchProyectos().finally(() => setIsLoading(false))
  }, [])

  // Cargar usuarios una vez y ordenarlos (más recientes primero)
  useEffect(() => {
    axios.get(`${BASE_URL}/todos-usuarios`)
      .then(res => {
        const list = Array.isArray(res.data.usuarios) ? res.data.usuarios : []
        const getTs = (u) => {
          const candidates = [u.createdAt, u.fecha_alta, u.fechaAlta, u.created_at, u.updatedAt, u.fecha_actualizacion]
          for (const c of candidates) {
            const t = c ? new Date(c).getTime() : NaN
            if (!Number.isNaN(t)) return t
          }
          const n = Number(u.id)
          return Number.isFinite(n) ? n : 0
        }
        const sorted = [...list].sort((a, b) => getTs(b) - getTs(a))
        setUsers(sorted)
      })
      .catch(err => console.error(err))
  }, [])

  // --- Update estados (incluye usuario_autorizado y facturado) ---
  const updateEstado = async (project, field, newValue) => {
    // Guardado optimista en UI
    setProyectos(prev => prev.map(p => p.id === project.id ? { ...p, [field]: newValue } : p))

    const payload = {
      prototipo: field === 'prototipo' ? newValue : project.prototipo,
      diseno: field === 'diseno' ? newValue : project.diseno,
      desarrollo: field === 'desarrollo' ? newValue : project.desarrollo,
      contenido: field === 'contenido' ? newValue : project.contenido,
      comprobacion_final: field === 'comprobacion_final' ? newValue : project.comprobacion_final,
      usuario_autorizado: field === 'usuario_autorizado' ? newValue : project.usuario_autorizado,
      facturado: field === 'facturado' ? newValue : (typeof project.facturado === 'undefined' ? 0 : project.facturado)
    }

    try {
      const response = await axios.put(`${BASE_URL}/actualizar-estados/${project.id}`, payload)
      setProyectos(prev => prev.map(p => p.id === project.id ? response.data.proyecto : p))
    } catch (error) {
      console.error('Error al actualizar estado:', error)
      fetchProyectos()
    }
  }

  const parseDate = (dateStr) => {
    if (!dateStr || dateStr === 'por_definir') return null
    const [day, month, year] = dateStr.split('/')
    const d = new Date(Number(year), Number(month) - 1, Number(day))
    return isNaN(d.getTime()) ? null : d
  }

  const visibleProyectos = useMemo(() => {
    const q = searchText.trim().toLowerCase()
    const byTexto = (p) => !q || [p.nombre, p.usuario_autorizado].join(' ').toLowerCase().includes(q)
    const byTipo = (p) => {
      if (!tipoFilter || tipoFilter === 'Todos') return true
      return (p.tipo_de_proyecto || '').toLowerCase() === tipoFilter.toLowerCase()
    }
    const byArchivado = (p) => {
      if (archFilter === 'todos') return true
      if (archFilter === 'si') return Number(p.archivado) === 1
      return Number(p.archivado) === 0 || p.archivado === false || p.archivado === null || typeof p.archivado === 'undefined'
    }
    const filtered = (proyectos || []).filter(p => byTexto(p) && byTipo(p) && byArchivado(p))
    const sorted = [...filtered].sort((a, b) => {
      const da = parseDate(a.inicio_proyecto)
      const db = parseDate(b.inicio_proyecto)
      const ta = da ? da.getTime() : -Infinity
      const tb = db ? db.getTime() : -Infinity
      return sortOrder === 'newest' ? tb - ta : ta - tb
    })
    return sorted
  }, [proyectos, searchText, tipoFilter, archFilter, sortOrder])

  // ---------- Modal de selección de usuario ----------
  const openUserModal = (project) => {
    setUserModalProject(project)
    setUserQuery('')
    setUserPage(1)
    setUserModalOpen(true)
  }
  const closeUserModal = () => {
    setUserModalOpen(false)
    setUserModalProject(null)
    setUserQuery('')
    setUserPage(1)
  }

  useEffect(() => { setUserPage(1) }, [userQuery])

  const fullFilteredUsers = useMemo(() => {
    const q = userQuery.trim().toLowerCase()
    if (!q) return users
    return users.filter(u =>
      (u.usuario && u.usuario.toLowerCase().includes(q)) ||
      (u.nombre && u.nombre.toLowerCase().includes(q)) ||
      (u.email && u.email.toLowerCase().includes(q))
    )
  }, [users, userQuery])

  const visibleUsers = useMemo(() => fullFilteredUsers.slice(0, userPage * 10), [fullFilteredUsers, userPage])
  const hasMoreUsers = visibleUsers.length < fullFilteredUsers.length

  const assignUser = async (username) => {
    if (!userModalProject) return
    await updateEstado(userModalProject, 'usuario_autorizado', username || '')
    closeUserModal()
  }

  // Flags de vista por tipo
  const isDisenoView = (tipoFilter || '').toLowerCase() === 'diseno'
  const isMarketingView = (tipoFilter || '').toLowerCase() === 'marketing'
  const tipoKey = (tipoFilter || 'Todos').toLowerCase()
  const gridClass = `proy-grid-${(isDisenoView || isMarketingView) ? 'reducida' : 'completa'}`

  if (isLoading) return <div className="loader-fullscreen"><Loader /></div>

  return (
    <main className="site-width">
      <button className="boton-volver" onClick={() => navigate('/dashboard')}>
        ← Volver
      </button>
      <div className="grid-tracking-proyectos">
        <div className="pagina-cabecera">
          <h2>Tracking de proyectos</h2>
          <p>Los proyectos se sincronizan cada 10 minutos con Holded.</p>
        </div>

        {/* --- Barra de herramientas --- */}
        <div className="proy-toolbar">
          <input
            type="text"
            placeholder="Buscar por nombre de proyecto o cliente…"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          <div className="proy-toolbar-acciones">
            <select value={sortOrder} onChange={e => setSortOrder(e.target.value)}>
              <option value="newest">Más nuevo</option>
              <option value="oldest">Más antiguo</option>
            </select>
            <select value={tipoFilter} onChange={e => setTipoFilter(e.target.value)}>
              <option value="Todos">Todos los tipos</option>
              <option value="desarrollo">Desarrollo</option>
              <option value="diseno">Diseño</option>
              <option value="marketing">Marketing</option>
            </select>
            <select value={archFilter} onChange={e => setArchFilter(e.target.value)}>
              <option value="no">No archivados</option>
              <option value="si">Archivados</option>
              <option value="todos">Todos</option>
            </select>
          </div>
        </div>

        {/* --- Tabla --- */}
        <div className="overflow-tabla">
          <div className="proy-panel">
            <div className={`proy-list-header ${gridClass}`}>
              <span>Nombre</span>
              <span>Cliente autorizado</span>

              {!isDisenoView && !isMarketingView && (
                <>
                  <span>Prototipo</span>
                  <span>Diseño</span>
                  <span>Desarrollo</span>
                  <span>Contenido</span>
                  <span>Validación web</span>
                </>
              )}
              {isDisenoView && (<><span>Diseño</span><span>Facturado</span></>)}
              {isMarketingView && (<><span>Desarrollo</span><span>Facturado</span></>)}

              <span>Inicio</span>
              <span>Fin</span>
            </div>

            <div className="proy-rows">
              {visibleProyectos.length === 0 ? (
                <div className="proy-vacio">No hay proyectos que coincidan con los filtros.</div>
              ) : (
                visibleProyectos.map(proyecto => (
                  <div key={proyecto.id} className={`proy-row ${gridClass}`}>
                    <div className="proy-col proy-col-nombre">
                      <Link to={`/dashboard/proyecto/${proyecto.id}`}>{proyecto.nombre}</Link>
                    </div>

                    <div className="proy-col proy-col-usuario">
                      <span className="proy-user-chip">
                        {proyecto.usuario_autorizado ? `@${proyecto.usuario_autorizado}` : 'Sin asignar'}
                      </span>
                      <button type="button" className="proy-btn-asignar" onClick={() => openUserModal(proyecto)}>
                        {proyecto.usuario_autorizado ? 'Editar' : 'Asignar'}
                      </button>
                    </div>

                    {!isDisenoView && !isMarketingView && (
                      <>
                        <div className="proy-col">
                          <EstadoSelect value={proyecto.prototipo} options={estados} onChange={(v) => updateEstado(proyecto, 'prototipo', v)} />
                        </div>
                        <div className="proy-col">
                          <EstadoSelect value={proyecto.diseno} options={estados} onChange={(v) => updateEstado(proyecto, 'diseno', v)} />
                        </div>
                        <div className="proy-col">
                          <EstadoSelect value={proyecto.desarrollo} options={estados} onChange={(v) => updateEstado(proyecto, 'desarrollo', v)} />
                        </div>
                        <div className="proy-col">
                          <EstadoSelect value={proyecto.contenido} options={estados} onChange={(v) => updateEstado(proyecto, 'contenido', v)} />
                        </div>
                        <div className="proy-col">
                          <EstadoSelect value={proyecto.comprobacion_final} options={estados} onChange={(v) => updateEstado(proyecto, 'comprobacion_final', v)} />
                        </div>
                      </>
                    )}

                    {isDisenoView && (
                      <>
                        <div className="proy-col">
                          <EstadoSelect value={proyecto.diseno} options={estados} onChange={(v) => updateEstado(proyecto, 'diseno', v)} />
                        </div>
                        <div className="proy-col">
                          <FacturadoToggle
                            value={proyecto.facturado ?? 0}
                            disabled={!!savingFacturado[proyecto.id]}
                            onToggle={async (val) => {
                              setSavingFacturado(s => ({ ...s, [proyecto.id]: true }))
                              await updateEstado(proyecto, 'facturado', val)
                              setSavingFacturado(s => ({ ...s, [proyecto.id]: false }))
                            }}
                          />
                        </div>
                      </>
                    )}

                    {isMarketingView && (
                      <>
                        <div className="proy-col">
                          <EstadoSelect value={proyecto.desarrollo} options={estados} onChange={(v) => updateEstado(proyecto, 'desarrollo', v)} />
                        </div>
                        <div className="proy-col">
                          <FacturadoToggle
                            value={proyecto.facturado ?? 0}
                            disabled={!!savingFacturado[proyecto.id]}
                            onToggle={async (val) => {
                              setSavingFacturado(s => ({ ...s, [proyecto.id]: true }))
                              await updateEstado(proyecto, 'facturado', val)
                              setSavingFacturado(s => ({ ...s, [proyecto.id]: false }))
                            }}
                          />
                        </div>
                      </>
                    )}

                    <div className="proy-col proy-col-fecha">{proyecto.inicio_proyecto}</div>
                    <div className="proy-col proy-col-fecha">{proyecto.fin_proyecto}</div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* --------- Modal asignar usuario --------- */}
      <Popup
        open={userModalOpen}
        title="Asignar cliente autorizado"
        onClose={closeUserModal}
        showConfirm={false}
        footerActions={(
          <button className="proy-btn-quitar" onClick={() => assignUser('')}>
            <FiX /> Quitar asignación
          </button>
        )}
      >
        <div className="proy-modal-intro">
          <p>El usuario asignado tendrá acceso a ver el estado de su proyecto.</p>
          <div className="proy-modal-proyecto">
            <span className="proy-modal-proyecto-label">Proyecto</span>
            <span className="proy-modal-proyecto-nombre">{userModalProject?.nombre}</span>
          </div>
        </div>

        <div className="proy-user-search">
          <input
            type="text"
            placeholder="Busca por @usuario, nombre o email"
            value={userQuery}
            onChange={(e) => setUserQuery(e.target.value)}
            autoFocus
          />
        </div>

        <div className="proy-user-results">
          {visibleUsers.length === 0 && (
            <p className="proy-vacio">No hay resultados.</p>
          )}
          {visibleUsers.map(u => (
            <button key={u.id} type="button" className="proy-user-result-item" onClick={() => assignUser(u.usuario)}>
              <span className="proy-user-icon"><FiUser /></span>
              <span className="proy-user-info">
                <span className="proy-user-handle">@{u.usuario}</span>
                {u.email && <span className="proy-user-email">{u.email}</span>}
              </span>
            </button>
          ))}
        </div>

        {hasMoreUsers && (
          <button type="button" className="proy-btn-cargar-mas" onClick={() => setUserPage(p => p + 1)}>
            Cargar más
          </button>
        )}
      </Popup>
    </main>
  )
}

export default Proyectos
